import {
	IdentityProviderProtocol,
	type IdentityProviderResource,
	IdentityProviderStatus,
	type SamlProviderConfigurationView,
} from '@today/types'
import { EmptyState } from '@today/ui-components'
import { Badge, type BadgeProps, Button, Switch } from '@today/ui-primitives'

/** Where the IdP admin points their SAML app. */
export interface ServiceProviderValues {
	spEntityId: string
	acsUrl: string
}

/**
 * Props for {@link ProviderList}.
 *
 * @property providers - The tenant's providers, in the server's order (default first).
 * @property serviceProvider - The SP values to paste into the IdP.
 * @property isBusy - True while a request is in flight; every action is disabled.
 * @property onAdd - Opens the create form.
 * @property onEdit - Opens the edit form for a provider.
 * @property onToggleEnabled - Switches a provider on or off.
 * @property onTest - Runs a test login through a provider.
 * @property onRotate - Opens the certificate rotation for a SAML provider.
 * @property onOpenMapping - Opens a provider's attribute mapping (TO-377); omitted for viewers without `tenant.manage_idp`.
 */
export interface ProviderListProps {
	providers: IdentityProviderResource[]
	serviceProvider: ServiceProviderValues
	isBusy?: boolean
	onAdd: () => void
	onEdit: (provider: IdentityProviderResource) => void
	onToggleEnabled: (provider: IdentityProviderResource, enabled: boolean) => void
	onTest: (provider: IdentityProviderResource) => void
	onRotate: (provider: IdentityProviderResource) => void
	onOpenMapping?: (provider: IdentityProviderResource) => void
}

const STATUS_BADGE: Record<IdentityProviderStatus, { label: string; variant: BadgeProps['variant'] }> = {
	[IdentityProviderStatus.PENDING]: { label: 'Pending test login', variant: 'secondary' },
	[IdentityProviderStatus.VERIFIED]: { label: 'Verified', variant: 'success' },
}

/** The date part of an ISO timestamp. */
const dayOf = (iso: string) => iso.slice(0, 10)

/** Protocol, certificate expiry and the last test, on one line each. */
function ProviderFacts({ provider }: { provider: IdentityProviderResource }) {
	const isSaml = provider.protocol === IdentityProviderProtocol.SAML2
	const certificate = isSaml ? (provider.configuration as SamlProviderConfigurationView).certificate : null
	return (
		<ul className="flex flex-col gap-0.5 text-ds-sm text-ds-text-muted">
			<li>
				{isSaml ? 'SAML 2.0' : 'OpenID Connect'} · {provider.code}
			</li>
			{certificate ? <li>Certificate expires {dayOf(certificate.notAfter)}</li> : null}
			{isSaml && !certificate ? <li>Certificate managed by the metadata URL</li> : null}
			{provider.lastTest ? (
				<li>
					Last test login {provider.lastTest.passed ? 'passed' : 'failed'} on {dayOf(provider.lastTest.at)}
				</li>
			) : null}
		</ul>
	)
}

type RowProps = Omit<ProviderListProps, 'providers' | 'serviceProvider' | 'onAdd'> & {
	provider: IdentityProviderResource
}

/**
 * One provider: its badges, facts and actions. SAML and OIDC share the
 * lifecycle (TO-378); only SAML has a certificate to rotate, and a test login
 * needs the provider's Cognito name.
 */
function ProviderRow({ provider, isBusy = false, onEdit, onToggleEnabled, onTest, onRotate, onOpenMapping }: RowProps) {
	const isSaml = provider.protocol === IdentityProviderProtocol.SAML2
	const badge = STATUS_BADGE[provider.status]
	return (
		<li className="flex flex-col gap-3 rounded-ds-md border border-ds-border p-4" aria-label={provider.name}>
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-ds-base font-semibold text-ds-text">{provider.name}</span>
				<Badge variant={badge.variant}>{badge.label}</Badge>
				{provider.isDefault ? <Badge variant="primary">Default</Badge> : null}
			</div>
			<ProviderFacts provider={provider} />
			<div className="flex flex-wrap items-center gap-2">
				<Switch
					isSelected={provider.enabled}
					isDisabled={isBusy}
					onChange={(enabled) => onToggleEnabled(provider, enabled)}
				>
					Enabled
				</Switch>
				<Button
					variant="secondary"
					size="sm"
					isDisabled={isBusy || !provider.enabled || provider.cognitoProviderName === null}
					onPress={() => onTest(provider)}
				>
					Test login
				</Button>
				{isSaml ? (
					<Button variant="secondary" size="sm" isDisabled={isBusy} onPress={() => onRotate(provider)}>
						Rotate certificate
					</Button>
				) : null}
				<Button variant="secondary" size="sm" isDisabled={isBusy} onPress={() => onEdit(provider)}>
					Edit
				</Button>
				{onOpenMapping ? (
					<Button variant="secondary" size="sm" isDisabled={isBusy} onPress={() => onOpenMapping(provider)}>
						Attribute mapping
					</Button>
				) : null}
			</div>
		</li>
	)
}

/**
 * A tenant's identity providers (TO-410 REQ-005 / REQ-007): the status badge
 * (pending / verified), the default marker, the enable toggle, the certificate
 * expiry, and the test-login / rotate / edit actions, under the SP values the
 * IdP admin needs.
 *
 * Pure: the page's hook owns the requests and the test-login popup. Whether a
 * change is allowed (the lockout guard, the default rules) is the server's call; a
 * refusal is shown by the page.
 */
export function ProviderList({ providers, serviceProvider, onAdd, ...rowProps }: ProviderListProps) {
	return (
		<section className="flex flex-col gap-4" aria-label="Identity providers">
			<header className="flex items-center justify-between gap-4">
				<h3 className="text-ds-base font-semibold text-ds-text">Identity providers</h3>
				<Button variant="primary" size="sm" isDisabled={rowProps.isBusy} onPress={onAdd}>
					Add provider
				</Button>
			</header>
			<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-ds-sm">
				<dt className="text-ds-text-muted">SP entity ID</dt>
				<dd className="font-mono break-all">{serviceProvider.spEntityId}</dd>
				<dt className="text-ds-text-muted">ACS URL</dt>
				<dd className="font-mono break-all">{serviceProvider.acsUrl}</dd>
			</dl>
			{providers.length === 0 ? (
				<EmptyState title="No identity providers" description="Users sign in with email and password." />
			) : (
				<ul className="flex flex-col gap-3">
					{providers.map((provider) => (
						<ProviderRow key={provider.id} provider={provider} {...rowProps} />
					))}
				</ul>
			)}
		</section>
	)
}
